import { create } from 'zustand'
import { getAxiosInstance, axiosRequest } from '../network/axiosInstance'

// Define subscription interfaces
export interface SubscriptionLimits {
    aiActionsPerMonth: number
    documentsPerMonth: number
    projectsTotal: number
    collaboratorsPerProject: number
    storageGB: number
    prioritySupport: boolean
    customTemplates: boolean
    advancedAnalytics: boolean
    apiAccess: boolean
}

export interface UsageStats {
    currentPeriod: {
        aiActions: number
        documents: number
        projects: number
        storageUsed: number
    }
    periodStart: string
    periodEnd: string
}

export interface SubscriptionInfo {
    id?: string
    tier: 'FREE' | 'BASIC' | 'PRO' | 'ENTERPRISE' | 'BUSINESS' | 'BUSINESS_PRO' | 'HOBBYIST' | 'STUDENT' | 'PAY_AS_YOU_GO' | 'NO_TIER'
    status: 'active' | 'inactive' | 'pending' | 'expired' | 'cancelled'
    currentPeriodStart?: string
    currentPeriodEnd?: string
    cancelAtPeriodEnd?: boolean
    isOrganizationSubscription: boolean
    organizationId?: string
    limits: SubscriptionLimits
    usage: UsageStats
}

export interface ActionPackage {
    id: string
    name: string
    description: string
    actionsIncluded: number
    costUsd: number
    validityDays?: number
    isActive: boolean
    purchasedAt?: string
    expiresAt?: string
}

// Store for subscription data
export interface SubscriptionState {
    subscriptionLoading: boolean
    personalSubscription: SubscriptionInfo | null
    organizationSubscriptions: Record<string, SubscriptionInfo> // Key: organizationId, Value: subscription info
    subscriptionError: string | null
    packages: ActionPackage[]
    packagesLoading: boolean
    fetchSubscriptionInfo: (projectId?: string, organizationId?: string) => Promise<void>
    fetchOrganizationSubscription: (organizationId: string) => Promise<void>
    fetchActionPackages: () => Promise<void>
    // Helper methods to get current subscription based on context
    getPersonalSubscription: () => SubscriptionInfo | null
    getOrganizationSubscription: (organizationId: string) => SubscriptionInfo | null
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
    subscriptionLoading: false,
    personalSubscription: null,
    organizationSubscriptions: {},
    subscriptionError: null,
    packages: [],
    packagesLoading: false, fetchSubscriptionInfo: async (projectId?: string, organizationId?: string) => {
        set({ subscriptionLoading: true, subscriptionError: null })

        try {
            const axiosInstance = getAxiosInstance()

            // If organizationId is provided, delegate to fetchOrganizationSubscription
            if (organizationId) {
                await get().fetchOrganizationSubscription(organizationId)
                return
            }

            // Fetch personal subscription
            const url = projectId
                ? `/api/subscriptions/user?projectId=${projectId}`
                : `/api/subscriptions/user`

            const response = await axiosInstance.get(url)

            // Fetch real usage data for personal subscription
            let usageResponse
            try {
                usageResponse = await axiosInstance.get('/api/subscriptions/usage')
            } catch (usageError) {
                console.log('Could not fetch usage data, using fallback:', usageError)
                usageResponse = { data: { usage: { currentPeriod: { aiActions: 0, projects: 0, storageUsed: 0 } } } }
            }

            const realUsage = usageResponse.data?.usage?.currentPeriod || { aiActions: 0, projects: 0, storageUsed: 0 }

            // Transform backend response to match frontend SubscriptionInfo interface
            const backendData = response.data
            const subscription = backendData.subscription
            const limits = backendData.limits
            const usage = backendData.usage

            console.log('Backend personal subscription response:', {
                subscription,
                limits,
                usage,
                realUsage
            })

            const subscriptionInfo: SubscriptionInfo = {
                id: subscription?.id,
                tier: subscription?.tier || 'FREE',
                status: subscription?.isActive ? 'active' : 'inactive',
                currentPeriodStart: subscription?.activatedAt,
                currentPeriodEnd: subscription?.expiresAt,
                cancelAtPeriodEnd: subscription?.cancelledAt ? true : false,
                isOrganizationSubscription: false,
                organizationId: undefined,
                limits: {
                    aiActionsPerMonth: limits?.monthlyActions || subscription?.monthlyActions || 0,
                    documentsPerMonth: 0, // Not tracked separately in current backend
                    projectsTotal: limits?.maxAgents || subscription?.maxAgents || 0, // maxAgents represents active projects limit
                    collaboratorsPerProject: limits?.maxUsers || subscription?.maxUsers || 1,
                    storageGB: limits?.storageGB || 1, // Get from subscription limits
                    prioritySupport: subscription?.tier === 'PRO' || subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS' || subscription?.tier === 'BUSINESS_PRO',
                    customTemplates: subscription?.tier === 'PRO' || subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS' || subscription?.tier === 'BUSINESS_PRO',
                    advancedAnalytics: subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS_PRO',
                    apiAccess: subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS_PRO'
                },
                usage: {
                    currentPeriod: {
                        aiActions: realUsage.aiActions || usage?.actionsUsed || limits?.currentActions || subscription?.currentActions || 0,
                        documents: 0, // Not tracked separately
                        projects: realUsage.projects || 0, // Real projects count
                        storageUsed: realUsage.storageUsed || 0 // Real storage usage in GB
                    },
                    periodStart: usageResponse.data?.usage?.periodStart || subscription?.activatedAt || new Date().toISOString(),
                    periodEnd: usageResponse.data?.usage?.periodEnd || subscription?.expiresAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                }
            }

            set({
                subscriptionLoading: false,
                personalSubscription: subscriptionInfo,
            })
        } catch (error) {
            console.error('Error fetching subscription info:', error)
            set({
                subscriptionLoading: false,
                subscriptionError: (error as Error).message,
            })
        }
    },

    fetchOrganizationSubscription: async (organizationId: string) => {
        set({ subscriptionLoading: true, subscriptionError: null })

        try {
            const axiosInstance = getAxiosInstance()
            const response = await axiosInstance.get(`/api/subscriptions/organization/${organizationId}`)

            // Fetch real usage data for organization
            let usageResponse
            try {
                usageResponse = await axiosInstance.get(`/api/subscriptions/usage?organizationId=${organizationId}`)
            } catch (usageError) {
                console.log('Could not fetch organization usage data, using fallback:', usageError)
                usageResponse = { data: { usage: { currentPeriod: { aiActions: 0, projects: 0, storageUsed: 0 } } } }
            }

            const realUsage = usageResponse.data?.usage?.currentPeriod || { aiActions: 0, projects: 0, storageUsed: 0 }

            const backendData = response.data
            const subscription = backendData.subscription
            const limits = backendData.limits
            const usage = backendData.usage

            const subscriptionInfo: SubscriptionInfo = {
                id: subscription?.id,
                tier: subscription?.tier || 'NO_TIER',
                status: subscription?.isActive ? 'active' : 'inactive',
                currentPeriodStart: subscription?.activatedAt,
                currentPeriodEnd: subscription?.expiresAt,
                cancelAtPeriodEnd: subscription?.cancelledAt ? true : false,
                isOrganizationSubscription: true,
                organizationId: organizationId, limits: {
                    aiActionsPerMonth: limits?.monthlyActions || subscription?.monthlyActions || 0,
                    documentsPerMonth: 0,
                    projectsTotal: limits?.maxAgents || subscription?.maxAgents || 0, // Use maxAgents like personal subscription
                    collaboratorsPerProject: limits?.maxUsers || subscription?.maxUsers || 1,
                    storageGB: limits?.storageGB || 0, // Use 0 as default for NO_TIER organizations
                    prioritySupport: subscription?.tier === 'PRO' || subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS' || subscription?.tier === 'BUSINESS_PRO',
                    customTemplates: subscription?.tier === 'PRO' || subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS' || subscription?.tier === 'BUSINESS_PRO',
                    advancedAnalytics: subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS_PRO',
                    apiAccess: subscription?.tier === 'ENTERPRISE' || subscription?.tier === 'BUSINESS_PRO'
                },
                usage: {
                    currentPeriod: {
                        aiActions: realUsage.aiActions || usage?.actionsUsed || subscription?.currentActions || 0,
                        documents: 0,
                        projects: realUsage.projects || 0,
                        storageUsed: realUsage.storageUsed || 0
                    },
                    periodStart: usageResponse.data?.usage?.periodStart || subscription?.activatedAt || new Date().toISOString(),
                    periodEnd: usageResponse.data?.usage?.periodEnd || subscription?.expiresAt || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                }
            }

            set((state) => ({
                subscriptionLoading: false,
                organizationSubscriptions: {
                    ...state.organizationSubscriptions,
                    [organizationId]: subscriptionInfo
                }
            }))
        } catch (error) {
            console.error('Error fetching organization subscription:', error)
            set({
                subscriptionLoading: false,
                subscriptionError: (error as Error).message,
            })
        }
    },

    fetchActionPackages: async () => {
        set({ packagesLoading: true })

        try {
            const axiosInstance = getAxiosInstance()
            const response = await axiosInstance.get(`/api/subscriptions/packages`)

            // Backend returns { packages: [] }, extract the packages array
            const packagesData = response.data?.packages || []

            set({
                packagesLoading: false,
                packages: Array.isArray(packagesData) ? packagesData : [],
            })
        } catch (error) {
            console.error('Error fetching action packages:', error)
            set({
                packagesLoading: false,
                packages: [],
            })
        }
    },

    // Helper methods to get current subscription based on context
    getPersonalSubscription: () => {
        const state = get()
        return state.personalSubscription
    },

    getOrganizationSubscription: (organizationId: string) => {
        const state = get()
        return state.organizationSubscriptions[organizationId] || null
    },
}))

// Hook functions for subscription operations
export const useGetUserSubscription = axiosRequest({
    method: 'GET',
    url: '/api/subscriptions/user',
})

export const useGetUsageStats = axiosRequest({
    method: 'GET',
    url: '/api/subscriptions/usage',
})

export const useLogAction = axiosRequest({
    method: 'POST',
    url: '/api/subscriptions/action-log',
})

export const useCheckQuota = axiosRequest({
    method: 'POST',
    url: '/api/subscriptions/quota-check',
})

export const useGetActionPackages = axiosRequest({
    method: 'GET',
    url: '/api/subscriptions/packages',
})

export const usePurchasePackage = axiosRequest({
    method: 'POST',
    url: '/api/subscriptions/purchase-actions',
})

export const useUpgradeSubscription = axiosRequest({
    method: 'PUT',
    url: '/api/subscriptions/user',
})

// Hook for upgrading organization subscription
export const useUpgradeOrganizationSubscription = () => {
    return {
        execute: async ({ data, onSuccess, onError }: {
            data: { organizationId: string; tier: string }
            onSuccess: () => void
            onError: (error: any) => void
        }) => {
            try {
                const axiosInstance = getAxiosInstance()
                const response = await axiosInstance.put(`/api/subscriptions/organization/${data.organizationId}`, {
                    tier: data.tier
                })

                if (response.status === 200) {
                    onSuccess()
                }
            } catch (error: any) {
                console.error('Error upgrading organization subscription:', error)
                onError(error.response?.data || error)
            }
        }
    }
}
